// The tabbed report grid: sortable sticky header, optional grouping,
// a totals row that covers every matching record, and batch loading.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlignJustify, Eye } from "feather-icons-react/build/IconComponents";
import { Caret } from "./ReportParts";
import { dmy, hueOf, initialsOf, intg, money, num, pct2 } from "./reportFormat";

export const BATCH = 50;

// which columns carry figures (summed in the footer, right-aligned, sorted numerically)
export const isNumCol = (c) => c.type === "money" || c.type === "int";

const cellContent = (row, c, grand) => {
  if (c.cell) return c.cell(row);
  const v = row[c.k];
  switch (c.type) {
    case "index":
      return row.__sl;
    case "date":
      return v ? dmy(v) : "—";
    case "code":
      return v || "—";
    case "avatar": {
      const name = v == null || v === "" ? "—" : String(v);
      return (
        <div className="axr-cust">
          <span className="axr-av" style={{ "--h": hueOf(name) }}>
            {initialsOf(name)}
          </span>
          <span className="axr-t">{name}</span>
        </div>
      );
    }
    case "pill": {
      const p = c.pill ? c.pill(row) : { text: v, tone: "" };
      return p && p.text ? (
        <span className={"axr-pill " + (p.tone || "")}>
          <i />
          {p.text}
        </span>
      ) : (
        "—"
      );
    }
    case "share": {
      const base = num(row[c.shareOf || "net"]);
      const pctv = grand > 0 ? (base / grand) * 100 : 0;
      return (
        <div className="axr-share">
          <span className="axr-sb">
            <i style={{ width: `${Math.min(100, Math.max(0, pctv))}%` }} />
          </span>
          <span className="axr-st">{pct2(pctv)}%</span>
        </div>
      );
    }
    case "int":
      return intg(v);
    case "money":
      return money(v);
    default:
      return v == null || v === "" ? "—" : String(v);
  }
};

const cellClass = (c, row) => {
  switch (c.type) {
    case "index":
      return "axr-idx";
    case "date":
      return "axr-date";
    case "code":
      return "axr-bill";
    case "int":
      return "axr-num";
    case "money":
      return c.k === "net" || c.hero ? "axr-net" : "axr-num" + (num(row[c.k]) === 0 ? " muted" : "");
    default:
      return "";
  }
};

const ReportTable = ({
  tabs,
  activeTab,
  onTabChange,
  columns,
  list,
  sort,
  onSort,
  groupOptions,
  groupBy,
  onGroupChange,
  groupOf,
  dense,
  onDenseToggle,
  rowKey,
  selectedKey,
  onRowClick,
  shareKey = "net",
  emptyTitle = "Nothing in this selection",
  emptyHint = "Widen the period, or clear a filter or two.",
}) => {
  const wrapRef = useRef(null);
  const sentinelRef = useRef(null);
  const [shown, setShown] = useState(BATCH);

  const grouping = !!groupBy && !!groupOf;
  const grand = useMemo(() => list.reduce((a, r) => a + num(r[shareKey]), 0), [list, shareKey]);

  useEffect(() => {
    setShown(BATCH);
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
  }, [list]);

  useEffect(() => {
    const root = wrapRef.current;
    const target = sentinelRef.current;
    if (!root || !target || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setShown((s) => Math.min(s + BATCH, list.length));
      },
      { root, rootMargin: "260px" }
    );
    io.observe(target);
    return () => io.disconnect();
    // re-observing after each batch re-checks a sentinel that is still in view
  }, [list, shown]);

  // footer totals cover every matching row, not only the loaded ones
  const totals = useMemo(() => {
    const acc = {};
    columns.forEach((c) => {
      if (isNumCol(c) && !c.noSum) acc[c.k] = list.reduce((a, r) => a + num(r[c.k]), 0);
    });
    return acc;
  }, [list, columns]);

  const groupInfo = useMemo(() => {
    if (!grouping) return {};
    const info = {};
    list.forEach((r) => {
      const g = groupOf(r);
      if (!info[g]) {
        info[g] = { n: 0 };
        columns.forEach((c) => isNumCol(c) && (info[g][c.k] = 0));
      }
      info[g].n += 1;
      columns.forEach((c) => isNumCol(c) && (info[g][c.k] += num(r[c.k])));
    });
    return info;
  }, [list, columns, grouping, groupOf]);

  const lead = useMemo(() => {
    let i = 0;
    while (i < columns.length && !isNumCol(columns[i])) i++;
    return Math.max(1, i);
  }, [columns]);

  const hasActions = !!onRowClick;
  const body = [];
  let lastGroup = null;

  list.slice(0, shown).forEach((r, i) => {
    if (grouping) {
      const g = groupOf(r);
      if (g !== lastGroup) {
        const info = groupInfo[g] || {};
        body.push(
          <tr className="axr-grp" key={`g-${g}-${i}`}>
            <td colSpan={lead}>
              <div className="axr-gname">
                {g}
                <span className="axr-gn">
                  {info.n} record{info.n === 1 ? "" : "s"}
                </span>
              </div>
            </td>
            {columns.slice(lead).map((c) =>
              isNumCol(c) ? (
                <td key={c.k} className="axr-num">
                  {c.type === "int" ? intg(info[c.k]) : money(info[c.k])}
                </td>
              ) : (
                <td key={c.k} />
              )
            )}
            {hasActions && <td />}
          </tr>
        );
        lastGroup = g;
      }
    }
    const key = rowKey ? rowKey(r) : r.key || i;
    body.push(
      <tr
        key={key}
        className={selectedKey !== undefined && key === selectedKey ? "sel" : ""}
        tabIndex={hasActions ? 0 : undefined}
        onClick={hasActions ? () => onRowClick(r) : undefined}
        onKeyDown={hasActions ? (e) => e.key === "Enter" && onRowClick(r) : undefined}
      >
        {columns.map((c) => (
          <td key={c.k} className={cellClass(c, r)}>
            {cellContent(r, c, grand)}
          </td>
        ))}
        {hasActions && (
          <td>
            <div className="axr-rowacts">
              <button type="button" className="axr-rowbtn" title="View details" aria-label="View details">
                <Eye />
              </button>
            </div>
          </td>
        )}
      </tr>
    );
  });

  const left = list.length - Math.min(shown, list.length);

  return (
    <section className="axr-card axr-tablecard">
      {tabs.length > 1 && (
        <nav className="axr-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              type="button"
              key={t.id}
              className="axr-tab"
              role="tab"
              aria-selected={activeTab === t.id}
              onClick={() => onTabChange(t.id)}
            >
              {t.label} <span className="axr-n">{intg(t.count)}</span>
            </button>
          ))}
        </nav>
      )}

      <div className="axr-tbar">
        {groupOptions && groupOptions.length > 0 && (
          <>
            <span className="axr-lbl">Group by</span>
            <div className={"axr-minisel" + (groupBy ? " on" : "")}>
              <select value={groupBy} onChange={(e) => onGroupChange(e.target.value)} aria-label="Group by">
                <option value="">None</option>
                {groupOptions.map((g) => (
                  <option key={g.v} value={g.v}>
                    {g.t}
                  </option>
                ))}
              </select>
              <Caret />
            </div>
          </>
        )}
        <button
          type="button"
          className="axr-iconbtn"
          aria-pressed={dense}
          onClick={onDenseToggle}
          title="Compact rows"
        >
          <AlignJustify />
          Compact
        </button>
        <div className="axr-right">
          <span className="axr-count">
            <b>{intg(list.length)}</b> rows
            {grouping ? ` · grouped by ${groupBy}` : ""}
          </span>
        </div>
      </div>

      <div className="axr-tablewrap" ref={wrapRef}>
        <table className="axr-rpt">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.k}
                  className={isNumCol(c) || c.type === "share" ? "r" : ""}
                  style={
                    c.type === "index"
                      ? { width: Math.max(40, 22 + String(list.length).length * 9) }
                      : c.w
                      ? { width: c.w }
                      : undefined
                  }
                >
                  <button type="button" onClick={() => onSort(c)}>
                    {c.t}
                    {sort.k === (c.sortKey || c.k) && (
                      <span className="axr-ar">{sort.d > 0 ? "▲" : "▼"}</span>
                    )}
                  </button>
                </th>
              ))}
              {hasActions && <th style={{ width: 48 }} />}
            </tr>
          </thead>
          <tbody>{body}</tbody>
          {list.length > 0 && (
            <tfoot>
              <tr>
                {columns.map((c, i) => {
                  if (isNumCol(c) && !c.noSum) {
                    return (
                      <td key={c.k} className={"r" + (c.k === "net" || c.hero ? " axr-net" : "")}>
                        {c.type === "int" ? intg(totals[c.k]) : money(totals[c.k])}
                      </td>
                    );
                  }
                  if (i === 0) {
                    return (
                      <td key={c.k} className="axr-flbl">
                        Total · {intg(list.length)} rows
                      </td>
                    );
                  }
                  return <td key={c.k} />;
                })}
                {hasActions && <td />}
              </tr>
            </tfoot>
          )}
        </table>
        <div className="axr-sentinel" ref={sentinelRef} />
        {list.length === 0 && (
          <div className="axr-empty">
            <div className="axr-big">{emptyTitle}</div>
            <div>{emptyHint}</div>
          </div>
        )}
      </div>

      <div className="axr-pager">
        <span className="axr-info">
          {list.length ? (
            <>
              Showing <b>{intg(Math.min(shown, list.length))}</b> of <b>{intg(list.length)}</b>
            </>
          ) : (
            "nothing to show"
          )}
        </span>
        {list.length > 0 && (
          <span className="axr-info">
            {left > 0 ? `· ${BATCH} per batch, scroll for more` : "· all loaded"}
          </span>
        )}
        <div className="axr-mid">
          {left > 0 && (
            <button
              type="button"
              className="axr-pgb"
              onClick={() => setShown((s) => Math.min(s + BATCH, list.length))}
            >
              Load {Math.min(BATCH, left)} more
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReportTable;
